import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hooks/hooks";
import { dataSearchStatus } from "../../../store/dataSearchSlice";
import { useLoader } from "../../../hooks/common/useLoader";
import { ListFilter } from "../../../layout/content/list/ListFilter";
import excelIcon from "../../../assets/images/button_icons/excel_icon.svg";
import { useDataSearchFilters } from "../hooks/useDataSearchFilters";
import { useTestResultWideList } from "../hooks/useTestResultWideList";
import { useTestResultWideDownloader } from "../hooks/useTestResultWideDownloader";
import { TestResultSearchCondition } from "../../../types/dataSearch";

export function TestResultWideSearch() {
  const { t } = useTranslation("dataSearch");
  const status = useAppSelector(dataSearchStatus);

  useLoader({ status });

  // 가로 조회는 문항 컬럼이 60개 이상이라 표(헤더·행)를 화면에 그리면 렉/메모리 초과가 난다.
  // → 표 자체를 렌더하지 않고 필터 + 총 건수 + 전체 엑셀 다운로드 버튼만 둔다. 데이터는 엑셀로만 받는다.
  const { searchTestResultWideList, totalCnt, testResultWideList, getSearchParam } = useTestResultWideList({});
  const { filters } = useDataSearchFilters({ search: searchTestResultWideList({}) });
  const { downloadTestResultWideChunk } = useTestResultWideDownloader();

  // 전체 다운로드 시 한 파일에 담을 행 수(사용자가 선택). 서버 메모리 보호를 위해 청크로 나눠 여러 파일로 받는다.
  const CHUNK_OPTIONS = [1000, 1500, 3000, 5000];

  const [chunkSize, setChunkSize] = useState(3000);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // 현재 필터 기준 전체 건수(필터 변경 시 응답의 totalElements 가 갱신됨).
  const totalElements = testResultWideList?.totalElements ?? totalCnt ?? 0;

  // 전체 다운로드: 전체 건수를 청크 크기로 나눠 페이지별로 순차 다운로드(파일 여러 개).
  const onClickDownloadAll = useCallback(async () => {
    if (downloading) return;
    if (totalElements <= 0) {
      alert("조회된 데이터가 없습니다.");
      return;
    }
    const pages = Math.ceil(totalElements / chunkSize);
    const ok = window.confirm(
      `총 ${totalElements.toLocaleString()}건을 ${chunkSize.toLocaleString()}건씩 ${pages}개 파일로 나눠 다운로드합니다.\n` +
        `창을 닫지 말고 잠시 기다려주세요. 진행할까요?`
    );
    if (!ok) return;

    const baseParam = getSearchParam() as unknown as Record<string, unknown>;
    setDownloading(true);
    setProgress({ current: 0, total: pages });
    try {
      for (let page = 0; page < pages; page += 1) {
        const condition = { ...baseParam, page, size: chunkSize } as unknown as TestResultSearchCondition;
        // 순차 다운로드(서버 동시 부하 방지·브라우저 다중 다운로드 차단 회피)
        // eslint-disable-next-line no-await-in-loop
        await downloadTestResultWideChunk(condition);
        setProgress({ current: page + 1, total: pages });
      }
    } catch (e) {
      console.error(e);
      alert("다운로드 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setDownloading(false);
    }
  }, [downloading, totalElements, chunkSize, getSearchParam, downloadTestResultWideChunk]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <ListFilter filters={filters} doSearch={() => searchTestResultWideList({})()} />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          padding: "16px 20px",
          background: "#f7f8fa",
          borderRadius: 8,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          검색 결과 <span style={{ color: "#2f6fed" }}>{totalElements.toLocaleString()}</span>건
        </span>

        <div style={{ flex: 1 }} />

        <label style={{ fontSize: 13, color: "#555" }}>
          파일당&nbsp;
          <select value={chunkSize} disabled={downloading} onChange={(e) => setChunkSize(Number(e.target.value))}>
            {CHUNK_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n.toLocaleString()}건
              </option>
            ))}
          </select>
        </label>

        <button onClick={onClickDownloadAll} disabled={downloading}>
          <img src={excelIcon} alt={t("list.downloadExcel.iconAlt")} />
          {downloading ? `다운로드 중… (${progress.current}/${progress.total})` : "전체 엑셀 다운로드"}
        </button>
      </div>

      <div style={{ fontSize: 12, color: "#888", lineHeight: 1.5 }}>
        ※ 가로 조회는 화면에 데이터를 표시하지 않습니다(문항 컬럼이 많아 화면 렉 방지). 필터로 범위를 지정한 뒤 「전체 엑셀
        다운로드」로 받으세요. 건수가 많으면 파일당 개수만큼 여러 파일로 순서대로 다운로드됩니다.
      </div>
    </div>
  );
}
